import React, { useEffect, useMemo, useState } from "react";
import "../styles/AdminLeavesPage.css";
import "../styles/AdminTeamLeavesPage.css";
import { useI18n } from "../providers/I18nProvider";
import PaginationControls from "../components/PaginationControls";
import NoirAlert from "../components/NoirAlert";
import RejectLeaveDialog from "../components/RejectLeaveDialog";
import LeaveDetailsModal from "../components/LeaveDetailsModal";
import {
  TabBar,
  FilterBar,
  SearchInput,
  FilterField,
  EmptyState,
  countBy,
} from "../components/ListToolbar";
import { apiClient } from "../services/apiClient";

const normalizeRoleFromId = (roleId) => {
  const id = Number(roleId);
  if (id === 1) return "company_manager";
  if (id === 3) return "unit_supervisor";
  if (id === 4) return "hr";
  if (id === 6) return "admin";
  return "";
};

const normalizeRoleName = (value, fallbackRoleId = null) => {
  const raw = String(value || "").trim().toLowerCase();
  if (!raw) return normalizeRoleFromId(fallbackRoleId);
  if (raw === "manager") return "unit_supervisor";
  return raw;
};

const normalizeStage = (value) => {
  const raw = String(value || "").trim().toLowerCase();
  if (!raw) return "";
  return raw === "manager" ? "unit_supervisor" : raw;
};

const normalizeStatus = (value) => String(value || "").trim().toLowerCase();

const EDITABLE_APPROVAL_WINDOW_DAYS = 15;

const toDateKey = (value) => {
  if (!value) return "";
  const s = String(value);
  if (/^\\d{4}-\\d{2}-\\d{2}/.test(s)) return s.slice(0, 10);
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return "";
  return d.toISOString().slice(0, 10);
};

const isPendingApprovalEditableLeave = (leave) => {
  if (!leave) return false;
  if (normalizeStatus(leave.status) !== "beklemede") return false;
  if (normalizeStatus(leave.current_stage) === "final") return false;

  const startKey = toDateKey(leave.start_date);
  if (!startKey) return false;
  const createdAt = leave.created_at ? new Date(leave.created_at) : null;
  if (!createdAt || Number.isNaN(createdAt.getTime())) return false;

  const today = new Date();
  today.setUTCHours(0, 0, 0, 0);
  const startDateObj = new Date(`${startKey}T00:00:00Z`);
  if (startDateObj < today) return false;

  const windowMs = EDITABLE_APPROVAL_WINDOW_DAYS * 24 * 60 * 60 * 1000;
  if (Date.now() - createdAt.getTime() > windowMs) return false;

  return true;
};

const AdminTeamLeavesPage = () => {
  const { t, lang } = useI18n();
  const [leaves, setLeaves] = useState([]);
  const [leaveTypes, setLeaveTypes] = useState([]);
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [notification, setNotification] = useState({ message: "", type: "" });

  const [editLeave, setEditLeave] = useState(null);
  const [editForm, setEditForm] = useState({
    leave_type_id: "",
    start_date: "",
    end_date: "",
    start_time: "",
    end_time: "",
    half_day_period: "",
    reason: "",
  });

  const [detailLeaveId, setDetailLeaveId] = useState(null);
  const [detailInitial, setDetailInitial] = useState(null);

  const [tab, setTab] = useState("pending"); // mine | pending | approved | closed | all
  const [userFilter, setUserFilter] = useState("");
  const [teamFilter, setTeamFilter] = useState("");
  const [typeFilter, setTypeFilter] = useState("");
  const [search, setSearch] = useState("");
  const [fromDate, setFromDate] = useState("");
  const [toDate, setToDate] = useState("");
  const [sortKey, setSortKey] = useState("created_desc");
  const loadedOnceRef = React.useRef(false);

  const pageSizeKey = "pageSize.teamLeaves";
  const [pageSize, setPageSize] = useState(() => {
    const raw = localStorage.getItem(pageSizeKey);
    const parsed = raw ? parseInt(raw, 10) : 10;
    return [10, 20, 30].includes(parsed) ? parsed : 10;
  });
  const [page, setPage] = useState(1);
  const [roleName, setRoleName] = useState(null);
  const [myId, setMyId] = useState(null);
  const normalizedRole = useMemo(() => normalizeRoleName(roleName, null) || "", [roleName]);
  const approvalRole = normalizedRole === "admin" ? "company_manager" : normalizedRole;

  useEffect(() => {
    localStorage.setItem(pageSizeKey, String(pageSize));
    setPage(1);
  }, [pageSize]);

  useEffect(() => {
    apiClient
      .get("/users/me")
      .then((res) => {
        const resolvedRole =
          normalizeRoleName(res.data?.role_name, res.data?.role_id) ||
          normalizeRoleFromId(res.data?.role_id) ||
          "employee";
        setRoleName(resolvedRole);
        setMyId(res.data?.id ?? null);
      })
      .catch(() => {
        // ignore
      });
  }, []);

  const formatDate = (dateStr) => {
    if (!dateStr) return "";
    const locale = lang === "tr" ? "tr-TR" : "en-US";
    return new Date(dateStr).toLocaleDateString(locale);
  };

  const formatLeaveDuration = (leave) => {
    const hourlyValue = Number(leave?.total_duration_hours);
    if (Number.isFinite(hourlyValue) && hourlyValue > 0) {
      return `${hourlyValue} ${t("leave.summary.hours")}`;
    }

    const dayValue = Number(leave?.calculated_days);
    if (Number.isFinite(dayValue) && dayValue > 0) {
      return `${dayValue} ${t("leave.summary.days")}`;
    }

    if (leave?.start_time && leave?.end_time) {
      return `${String(leave.start_time).slice(0, 5)} - ${String(leave.end_time).slice(0, 5)}`;
    }

    return "—";
  };

  const prettyStatus = (value) => {
    if (!value) return "—";
    const lower = String(value).toLowerCase();
    if (lower === "beklemede") return t("leave.status.pending");
    if (lower === "onaylandı") return t("leave.status.approved");
    if (lower === "reddedildi") return t("leave.status.rejected");
    if (lower === "atlandı") return t("leave.status.skipped");
    return value;
  };

  const prettyStageName = (stage) => {
    if (!stage) return "—";
    const normalized = stage === "manager" ? "unit_supervisor" : stage;
    const map = {
      unit_supervisor: t("leave.stage.unitSupervisor"),
      company_manager: t("leave.stage.companyManager"),
      hr: t("leave.stage.hr"),
    };
    return map[normalized] || normalized;
  };

  const canActOnLeave = (leave) => {
    if (!leave || normalizeStatus(leave.status) !== "beklemede") return false;
    const stage = normalizeStage(leave.current_stage);
    const role = approvalRole;
    if (!stage || !role) return false;
    if (role === "company_manager") {
      if (stage === "company_manager") return true;
      // Birim amiri aşamasını, atanmış amir/ekip yöneticisi olan company_manager
      // da onaylayabilir (backend ile tutarlı).
      if (stage === "unit_supervisor") {
        if (myId == null) return false;
        return (
          (leave.manager_id != null && Number(leave.manager_id) === Number(myId)) ||
          (leave.team_manager_id != null && Number(leave.team_manager_id) === Number(myId))
        );
      }
      return false;
    }
    if (role === "hr") return stage === "hr";
    if (role === "unit_supervisor") {
      if (stage !== "unit_supervisor") return false;
      return true; // backend enforces ownership/assignment
    }
    return false;
  };

  const busyIdsRef = React.useRef(new Set());
  const updateLeaveStatus = async (id, status, reason = null) => {
    if (busyIdsRef.current.has(id)) return; // çift tıklama koruması
    busyIdsRef.current.add(id);
    try {
      const response = await apiClient.patch(`/leaves/${id}/status`, { status, reason });
      const updated = response.data;
      setLeaves((prev) => prev.map((l) => (l.id === id ? { ...l, ...updated } : l)));
      const warningMsg =
        Array.isArray(updated?.warnings) && updated.warnings[0]?.message
          ? updated.warnings[0].message
          : "";
      if (warningMsg) {
        setNotification({ message: warningMsg, type: "warning" });
      } else {
        setNotification({ message: t("common.saved"), type: "success" });
      }
    } catch (err) {
      setNotification({
        message: err.response?.data?.error || err.response?.data?.msg || err.message || t("common.genericError"),
        type: "error",
      });
    } finally {
      busyIdsRef.current.delete(id);
    }
  };

  const handleApprove = (id) => updateLeaveStatus(id, "Onaylandı");
  const [rejectLeave, setRejectLeave] = useState(null);
  const handleReject = (id) => {
    setRejectLeave(leaves.find((l) => l.id === id) || null);
  };

  const openEdit = (leave) => {
    setEditLeave(leave);
    setEditForm({
      leave_type_id: leave?.leave_type_id ? String(leave.leave_type_id) : "",
      start_date: toDateKey(leave?.start_date),
      end_date: toDateKey(leave?.end_date),
      start_time: leave?.start_time ? String(leave.start_time).slice(0, 5) : "",
      end_time: leave?.end_time ? String(leave.end_time).slice(0, 5) : "",
      half_day_period: leave?.half_day_period ? String(leave.half_day_period) : "",
      reason: leave?.reason || "",
    });
  };

  const closeEdit = () => {
    setEditLeave(null);
    setEditForm({
      leave_type_id: "",
      start_date: "",
      end_date: "",
      start_time: "",
      end_time: "",
      half_day_period: "",
      reason: "",
    });
  };

  const submitEdit = async (e) => {
    e.preventDefault();
    if (!editLeave) return;

    const leaveTypeId = Number(editForm.leave_type_id);
    if (!Number.isFinite(leaveTypeId) || !editForm.start_date || !editForm.reason?.trim()) {
      setNotification({ message: t("leave.edit.required"), type: "error" });
      return;
    }

    const selectedType = (leaveTypes || []).find((lt) => Number(lt.id) === leaveTypeId);
    const isHourly = Boolean(selectedType?.is_duration_flexible);
    const isAnnual = String(selectedType?.name || "")
      .trim()
      .toLocaleLowerCase("tr-TR")
      .startsWith("yıllık izin");
    const halfDayPeriod = String(editForm.half_day_period || "").trim().toUpperCase();
    const isHalfDay = !isHourly && isAnnual && (halfDayPeriod === "AM" || halfDayPeriod === "PM");
    if (isHourly) {
      if (!editForm.start_time || !editForm.end_time) {
        setNotification({ message: t("leave.edit.timeRequired"), type: "error" });
        return;
      }
    } else if (!isHalfDay && !editForm.end_date) {
      setNotification({ message: t("leave.edit.endDateRequired"), type: "error" });
      return;
    }

    const payload = {
      leave_type_id: leaveTypeId,
      start_date: editForm.start_date,
      end_date: isHourly || isHalfDay ? editForm.start_date : editForm.end_date,
      reason: editForm.reason,
      half_day_period: isHalfDay ? halfDayPeriod : null,
      ...(isHourly ? { start_time: editForm.start_time, end_time: editForm.end_time } : {}),
    };

    try {
      const url =
        normalizedRole === "hr" ? `/leaves/${editLeave.id}/pending-edit` : `/leaves/${editLeave.id}`;
      const response =
        normalizedRole === "hr"
          ? await apiClient.patch(url, payload)
          : await apiClient.put(url, payload);
      const updated = response.data;
      setLeaves((prev) => prev.map((l) => (l.id === editLeave.id ? { ...l, ...updated } : l)));
      closeEdit();
      setNotification({
        message: normalizedRole === "hr" ? t("admin.leaves.editRequested") : t("leave.edit.updateSuccess"),
        type: "success",
      });
    } catch (err) {
      setNotification({
        message: err?.response?.data?.msg || err?.message || t("common.genericError"),
        type: "error",
      });
    }
  };

  const approvePendingEdit = async (leaveId) => {
    try {
      const response = await apiClient.patch(`/leaves/${leaveId}/pending-edit/approve`, {});
      const updated = response.data;
      setLeaves((prev) => prev.map((l) => (l.id === leaveId ? { ...l, ...updated } : l)));
      setNotification({ message: t("admin.leaves.editApproved"), type: "success" });
    } catch (err) {
      setNotification({
        message: err?.response?.data?.msg || err?.message || t("common.genericError"),
        type: "error",
      });
    }
  };

  const rejectPendingEdit = async (leaveId) => {
    try {
      const response = await apiClient.patch(`/leaves/${leaveId}/pending-edit/reject`, {});
      const updated = response.data;
      setLeaves((prev) => prev.map((l) => (l.id === leaveId ? { ...l, ...updated } : l)));
      setNotification({ message: t("admin.leaves.editRejected"), type: "success" });
    } catch (err) {
      setNotification({
        message: err?.response?.data?.msg || err?.message || t("common.genericError"),
        type: "error",
      });
    }
  };

  const fetchAll = async () => {
    setError("");
    try {
      const [leavesRes, usersRes, typesRes] = await Promise.all([
        apiClient.get("/leaves/admin"),
        apiClient.get("/users"),
        apiClient.get("/leave-types"),
      ]);
      setLeaves(Array.isArray(leavesRes.data) ? leavesRes.data : []);
      setLeaveTypes(typesRes.data || []);
      const sortedUsers = [...(usersRes.data || [])].sort((a, b) =>
        `${a.name} ${a.surname}`.localeCompare(`${b.name} ${b.surname}`, lang === "tr" ? "tr-TR" : "en-US", {
          sensitivity: "base",
        })
      );
      setUsers(sortedUsers);
      loadedOnceRef.current = true;
    } catch (err) {
      const msg = err.response?.data?.msg || err.message || t("common.genericError");
      if (loadedOnceRef.current) setNotification({ message: msg, type: "error" });
      else setError(msg);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchAll();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const isPendingSt = (st) => normalizeStatus(st) === "beklemede";
  const isApprovedSt = (st) => normalizeStatus(st) === "onaylandı";
  const isClosedSt = (st) => {
    const k = normalizeStatus(st);
    return k === "reddedildi" || k === "iptal edildi" || k === "i̇ptal edildi";
  };

  const tabCounts = useMemo(
    () =>
      countBy(leaves, {
        mine: (l) => canActOnLeave(l),
        pending: (l) => isPendingSt(l.status),
        approved: (l) => isApprovedSt(l.status),
        closed: (l) => isClosedSt(l.status),
        all: () => true,
      }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [leaves, approvalRole, myId]
  );

  const teamOptions = useMemo(() => {
    const names = new Set();
    leaves.forEach((l) => l.team_name && names.add(l.team_name));
    return [...names].sort((a, b) => a.localeCompare(b, lang === "tr" ? "tr-TR" : "en-US"));
  }, [leaves, lang]);

  const filteredLeaves = useMemo(() => {
    const q = search.trim().toLocaleLowerCase(lang === "tr" ? "tr-TR" : "en-US");
    const ts = (v) => {
      const d = new Date(v);
      return Number.isNaN(d.getTime()) ? 0 : d.getTime();
    };
    const key = (v) => (v ? String(v).slice(0, 10) : "");
    const list = leaves.filter((l) => {
      if (tab === "mine" && !canActOnLeave(l)) return false;
      if (tab === "pending" && !isPendingSt(l.status)) return false;
      if (tab === "approved" && !isApprovedSt(l.status)) return false;
      if (tab === "closed" && !isClosedSt(l.status)) return false;
      if (userFilter && Number(l.user_id) !== Number(userFilter)) return false;
      if (teamFilter && l.team_name !== teamFilter) return false;
      if (typeFilter && String(l.leave_type_id) !== typeFilter) return false;
      const startKey = key(l.start_date);
      const endKey = key(l.end_date) || startKey;
      if (fromDate && endKey && endKey < fromDate) return false;
      if (toDate && startKey && startKey > toDate) return false;
      if (q) {
        const hay = `${l.user_name || ""} ${l.user_surname || ""} ${l.user_username || ""} ${
          l.leave_type_name || ""
        } ${l.team_name || ""}`.toLocaleLowerCase(lang === "tr" ? "tr-TR" : "en-US");
        if (!hay.includes(q)) return false;
      }
      return true;
    });
    const sorters = {
      created_desc: (a, b) => ts(b.created_at) - ts(a.created_at),
      created_asc: (a, b) => ts(a.created_at) - ts(b.created_at),
      start_asc: (a, b) => ts(a.start_date) - ts(b.start_date),
      start_desc: (a, b) => ts(b.start_date) - ts(a.start_date),
      name_asc: (a, b) =>
        `${a.user_name} ${a.user_surname}`.localeCompare(
          `${b.user_name} ${b.user_surname}`,
          lang === "tr" ? "tr-TR" : "en-US",
          { sensitivity: "base" }
        ),
    };
    return [...list].sort(sorters[sortKey] || sorters.created_desc);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [leaves, tab, userFilter, teamFilter, typeFilter, search, fromDate, toDate, sortKey, approvalRole, myId, lang]);

  const hasActiveFilter = Boolean(userFilter || teamFilter || typeFilter || search || fromDate || toDate);
  const resetFilters = () => {
    setUserFilter("");
    setTeamFilter("");
    setTypeFilter("");
    setSearch("");
    setFromDate("");
    setToDate("");
  };

  const waitingDays = (leave) => {
    const d = new Date(leave.created_at);
    if (Number.isNaN(d.getTime())) return null;
    return Math.max(0, Math.floor((Date.now() - d.getTime()) / 86400000));
  };

  useEffect(() => {
    const totalPages = Math.ceil((filteredLeaves.length || 0) / pageSize) || 1;
    if (page > totalPages) setPage(totalPages);
  }, [filteredLeaves.length, page, pageSize]);

  useEffect(() => {
    setPage(1);
  }, [tab, userFilter, teamFilter, typeFilter, search, fromDate, toDate, sortKey]);

  const pagedLeaves = useMemo(() => {
    const start = (page - 1) * pageSize;
    return filteredLeaves.slice(start, start + pageSize);
  }, [filteredLeaves, page, pageSize]);

  const handleExport = async () => {
    try {
      // Döküm, ekranda listelenenlerin aynısıdır: sekme + tüm filtreler + sıralama.
      const response = await apiClient.post(
        "/leaves/export.xlsx",
        { ids: filteredLeaves.map((l) => l.id) },
        { responseType: "blob" }
      );
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement("a");
      link.href = url;
      link.download = `izin-gecmisi-${new Date().toISOString().slice(0, 10)}.xlsx`;
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (err) {
      let message = err.response?.data?.msg || err.message || t("common.genericError");
      try {
        const blob = err.response?.data;
        if (blob && typeof blob.text === "function") {
          const text = await blob.text();
          try {
            const parsed = JSON.parse(text);
            message = parsed.msg || parsed.error || text;
          } catch (_) {
            message = text || message;
          }
        }
      } catch (_) {
        // ignore
      }
      setNotification({ message, type: "error" });
    }
  };

  if (loading) return <div className="team-leaves-page">{t("common.loading")}</div>;
  if (error) return <div className="team-leaves-page error">{t("common.error")}: {error}</div>;

  return (
    <div className="team-leaves-page">
      <NoirAlert
        message={notification.message}
        type={notification.type}
        onClose={() => setNotification({ message: "", type: "" })}
      />
      <div className="team-leaves-header">
        <div>
          <div className="eyebrow">{t("teamLeaves.eyebrow")}</div>
          <h2 className="admin-title">{t("teamLeaves.title")}</h2>
          <p className="muted">{t("teamLeaves.subtitle")}</p>
        </div>
        <div className="actions">
          <button className="ghost" type="button" onClick={fetchAll}>
            {t("teamLeaves.refresh")}
          </button>
          <button
            className="primary"
            type="button"
            onClick={handleExport}
            disabled={filteredLeaves.length === 0}
            title={t("teamLeaves.exportHint")}
          >
            {t("teamLeaves.export")} ({filteredLeaves.length})
          </button>
        </div>
      </div>

      <TabBar
        value={tab}
        onChange={setTab}
        ariaLabel={t("teamLeaves.title")}
        tabs={[
          { key: "mine", label: t("admin.leaves.tabs.mine"), count: tabCounts.mine, attention: true },
          { key: "pending", label: t("admin.leaves.tabs.pending"), count: tabCounts.pending },
          { key: "approved", label: t("admin.leaves.tabs.approved"), count: tabCounts.approved },
          { key: "closed", label: t("admin.leaves.tabs.closed"), count: tabCounts.closed },
          { key: "all", label: t("admin.leaves.tabs.all"), count: tabCounts.all },
        ]}
      />
      <FilterBar showReset={hasActiveFilter} onReset={resetFilters}>
        <SearchInput
          value={search}
          onChange={setSearch}
          placeholder={t("teamLeaves.filters.search")}
        />
        <select
          className="leaves-filter-input lt-input"
          value={userFilter}
          onChange={(e) => setUserFilter(e.target.value)}
          aria-label={t("teamLeaves.filters.user")}
        >
          <option value="">{t("teamLeaves.filters.allUsers")}</option>
          {users.map((u) => (
            <option key={u.id} value={u.id}>
              {u.name} {u.surname} ({u.username})
            </option>
          ))}
        </select>
        {teamOptions.length > 1 && (
          <select
            className="leaves-filter-input lt-input"
            value={teamFilter}
            onChange={(e) => setTeamFilter(e.target.value)}
            aria-label={t("teamLeaves.columns.team")}
          >
            <option value="">{t("teamLeaves.filters.allTeams")}</option>
            {teamOptions.map((name) => (
              <option key={name} value={name}>
                {name}
              </option>
            ))}
          </select>
        )}
        <select
          className="leaves-filter-input lt-input"
          value={typeFilter}
          onChange={(e) => setTypeFilter(e.target.value)}
          aria-label={t("admin.leaves.columns.type")}
        >
          <option value="">{t("admin.leaves.filters.allTypes")}</option>
          {leaveTypes.map((lt) => (
            <option key={lt.id} value={String(lt.id)}>
              {lt.name}
            </option>
          ))}
        </select>
        <FilterField label={t("admin.leaves.filters.from")}>
          <input type="date" value={fromDate} onChange={(e) => setFromDate(e.target.value)} />
        </FilterField>
        <FilterField label={t("admin.leaves.filters.to")}>
          <input type="date" value={toDate} onChange={(e) => setToDate(e.target.value)} />
        </FilterField>
        <select
          className="leaves-filter-input lt-input"
          value={sortKey}
          onChange={(e) => setSortKey(e.target.value)}
          aria-label={t("admin.leaves.filters.sort")}
        >
          <option value="created_desc">{t("admin.leaves.filters.sortCreatedDesc")}</option>
          <option value="created_asc">{t("admin.leaves.filters.sortCreatedAsc")}</option>
          <option value="start_asc">{t("admin.leaves.filters.sortStartAsc")}</option>
          <option value="start_desc">{t("admin.leaves.filters.sortStartDesc")}</option>
          <option value="name_asc">{t("teamLeaves.filters.sortName")}</option>
        </select>
      </FilterBar>

      {filteredLeaves.length === 0 ? (
        <EmptyState message={leaves.length === 0 ? t("teamLeaves.empty") : t("admin.leaves.emptyFiltered")} />
      ) : (
        <div className="admin-leaves-container">
          <PaginationControls
            totalItems={filteredLeaves.length}
            page={page}
            pageSize={pageSize}
            onPageChange={setPage}
            onPageSizeChange={setPageSize}
          />
          <div className="table-wrap">
            <table className="admin-leaves-table">
              <thead>
                <tr>
                  <th>{t("teamLeaves.columns.person")}</th>
                  <th>{t("teamLeaves.columns.username")}</th>
                  <th>{t("teamLeaves.columns.team")}</th>
                  <th>{t("teamLeaves.columns.type")}</th>
                  <th>{t("admin.leaves.createdAt")}</th>
                  <th>{t("teamLeaves.columns.start")}</th>
                  <th>{t("teamLeaves.columns.end")}</th>
                  <th>{t("teamLeaves.columns.duration")}</th>
                  <th>{t("teamLeaves.columns.status")}</th>
                  <th>{t("teamLeaves.columns.stage")}</th>
                  <th>{t("admin.leaves.columns.actions")}</th>
                </tr>
              </thead>
              <tbody>
                {pagedLeaves.map((leave) => (
                  <tr key={leave.id}>
                    <td className="cell-strong">
                      {leave.user_name} {leave.user_surname}
                    </td>
                    <td>{leave.user_username}</td>
                    <td>{leave.team_name || "—"}</td>
                    <td>
                      {leave.leave_type_name}
                      {leave.leave_sub_type_name ? ` (${leave.leave_sub_type_name})` : ""}
                    </td>
                    <td className="mono">{leave.created_at ? formatDate(leave.created_at) : "—"}</td>
                    <td className="mono">{formatDate(leave.start_date)}</td>
                    <td className="mono">{formatDate(leave.end_date)}</td>
                    <td>{formatLeaveDuration(leave)}</td>
                    <td>
                      <span
                        className={`status-pill status-${String(leave.status || "").toLowerCase()}`}
                      >
                        {prettyStatus(leave.status)}
                      </span>
                    </td>
                    <td>
                      {leave.status === "Beklemede" ? prettyStageName(leave.current_stage) : "—"}
                      {leave.status === "Beklemede" && waitingDays(leave) != null && (
                        <div className={`waiting-days${waitingDays(leave) >= 3 ? " late" : ""}`}>
                          {waitingDays(leave)} {t("admin.leaves.waitingDays")}
                        </div>
                      )}
                    </td>
                    <td>
                      <div className="actions">
                        <button
                          className="btn"
                          type="button"
                          onClick={() => {
                            setDetailInitial(leave);
                            setDetailLeaveId(leave.id);
                          }}
                        >
                          {t("admin.leaves.details")}
                        </button>
                        {canActOnLeave(leave) ? (
                          <>
                            <button
                              className="btn btn-approve"
                              type="button"
                              onClick={() => handleApprove(leave.id)}
                            >
                              {t("admin.leaves.approve")}
                            </button>
                            <button
                              className="btn btn-reject"
                              type="button"
                              onClick={() => handleReject(leave.id)}
                            >
                              {t("admin.leaves.reject")}
                            </button>
                          </>
                        ) : (
                          <span className="muted">{t("admin.leaves.noAction")}</span>
                        )}

                        {isPendingApprovalEditableLeave(leave) && ["company_manager", "admin"].includes(normalizedRole) && (
                          <button className="btn btn-edit" type="button" onClick={() => openEdit(leave)}>
                            {t("common.edit")}
                          </button>
                        )}
                        {isPendingApprovalEditableLeave(leave) && normalizedRole === "hr" && (
                          <button className="btn btn-edit" type="button" onClick={() => openEdit(leave)}>
                            {t("admin.leaves.requestEdit")}
                          </button>
                        )}

                        {["company_manager", "admin"].includes(normalizedRole) && leave.pending_edit_payload && (
                          <>
                            <button
                              className="btn btn-approve"
                              type="button"
                              onClick={() => approvePendingEdit(leave.id)}
                            >
                              {t("admin.leaves.approveEdit")}
                            </button>
                            <button
                              className="btn btn-reject"
                              type="button"
                              onClick={() => rejectPendingEdit(leave.id)}
                            >
                              {t("admin.leaves.rejectEdit")}
                            </button>
                          </>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <PaginationControls
            totalItems={filteredLeaves.length}
            page={page}
            pageSize={pageSize}
            onPageChange={setPage}
            onPageSizeChange={setPageSize}
          />
        </div>
      )}

      <RejectLeaveDialog
        leave={rejectLeave}
        onConfirm={(id, reason) => updateLeaveStatus(id, "Reddedildi", reason)}
        onClose={() => setRejectLeave(null)}
      />

      {detailLeaveId && (
        <LeaveDetailsModal
          leaveId={detailLeaveId}
          initial={detailInitial}
          onClose={() => {
            setDetailLeaveId(null);
            setDetailInitial(null);
          }}
        />
      )}

      {editLeave && (
        <div className="modal-backdrop" onMouseDown={closeEdit}>
          <div className="modal" onMouseDown={(e) => e.stopPropagation()}>
            <h3>
              {normalizedRole === "hr"
                ? t("admin.leaves.requestEdit")
                : t("leave.edit.modalTitle")}
            </h3>
            <form className="form-grid" onSubmit={submitEdit}>
              <label>
                {t("leave.form.leaveType")}
                <select
                  value={editForm.leave_type_id}
                  onChange={(e) => setEditForm((p) => ({ ...p, leave_type_id: e.target.value }))}
                >
                  <option value="">{t("leave.form.leaveTypeSelect")}</option>
                  {leaveTypes.map((lt) => (
                    <option key={lt.id} value={lt.id}>
                      {lt.name}
                    </option>
                  ))}
                </select>
              </label>
              <label>
                {t("leave.form.startDate")}
                <input
                  type="date"
                  value={editForm.start_date}
                  onChange={(e) => setEditForm((p) => ({ ...p, start_date: e.target.value }))}
                />
              </label>
              <label>
                {t("leave.form.endDate")}
                <input
                  type="date"
                  value={editForm.end_date}
                  onChange={(e) => setEditForm((p) => ({ ...p, end_date: e.target.value }))}
                />
              </label>
              <label>
                {t("leave.form.startTime")}
                <input
                  type="time"
                  value={editForm.start_time}
                  onChange={(e) => setEditForm((p) => ({ ...p, start_time: e.target.value }))}
                />
              </label>
              <label>
                {t("leave.form.endTime")}
                <input
                  type="time"
                  value={editForm.end_time}
                  onChange={(e) => setEditForm((p) => ({ ...p, end_time: e.target.value }))}
                />
              </label>
              <label className="span-2">
                {t("leave.form.reason")}
                <textarea
                  rows={3}
                  value={editForm.reason}
                  onChange={(e) => setEditForm((p) => ({ ...p, reason: e.target.value }))}
                />
              </label>
              <div className="form-actions">
                <button className="ghost" type="button" onClick={closeEdit}>
                  {t("common.cancel")}
                </button>
                <button className="btn btn-approve" type="submit">
                  {normalizedRole === "hr"
                    ? t("admin.leaves.sendEditRequest")
                    : t("leave.actions.update")}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminTeamLeavesPage;
